import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, picUrl, posterUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminUserTabs from "@/components/AdminUserTabs";
import {
  Share2, MousePointerClick, ArrowLeft, Loader2, ShieldCheck,
  Play, ChevronRight, Film, Download, Info, X as XIcon,
} from "lucide-react";

/**
 * Admin-only dashboard opened when an admin taps a Top Sharer row.
 * Shows every share this user emitted, broken down by video, and the
 * click-throughs those shares drove. Regular users on this route see a
 * "not allowed" banner (the backend rejects with 403 anyway).
 */
function fmtNum(n) {
  const v = Number(n) || 0;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
  return v.toString();
}
function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return ""; }
}

// Trigger a client-side CSV download. Escapes any field that could
// break a spreadsheet parser: commas, quotes, newlines. Kept small +
// dependency-free — the dataset never exceeds a few hundred rows.
function downloadCsv(filename, headers, rows) {
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.map(esc).join(",")];
  for (const r of rows) lines.push(r.map(esc).join(","));
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const PLATFORM_COLORS = {
  whatsapp: "#25D366",
  facebook: "#1877F2",
  twitter: "#1DA1F2",
  telegram: "#0088CC",
  messenger: "#7B0FFF",
  email: "#FFC300",
  sms: "#39FF14",
  copy_link: "#FF007F",
  native_sheet: "#00FFFF",
  unknown: "rgba(255,255,255,0.35)",
};

export default function UserShareAdmin() {
  const { user, loading: authLoading } = useAuth();
  const { username } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!username) return;
    // Wait for auth to settle before firing the admin request — otherwise
    // the request may leave without a Bearer token attached and 401 out,
    // which we surface as a misleading "admin-only" gate.
    if (authLoading) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(`/admin/user/${encodeURIComponent(username)}/share-activity`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (cancelled) return;
        setError(
          e?.response?.status === 403
            ? "This dashboard is admin-only"
            : e?.response?.data?.detail || "Failed to load"
        );
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [username, authLoading]);

  // Backend `require_admin` is the source of truth — a stale client
  // `user.is_admin` (mobile Safari can hold it across nav) should not
  // reject a valid admin whose sidebar shows admin links. Rely on the
  // API response instead: 200 → render, 403 → error branch below.
  //
  // (previously gated on `!user.is_admin` here)

  const totals = data?.totals || {};
  const videos = data?.videos || [];
  const platforms = totals.platforms || {};
  const platformSum = Object.values(platforms).reduce((a, b) => a + (Number(b) || 0), 0);

  return (
    <div className="min-h-screen bg-black text-white pb-24">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div>
          <Link
            to="/search"
            data-testid="admin-share-back"
            className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-white/40 hover:text-white/70 mb-2"
          >
            <ArrowLeft className="h-3 w-3" /> search
          </Link>
          <div className="flex items-center gap-3">
            {data?.user?.picture ? (
              <img src={picUrl(data.user.picture)} alt="" className="h-12 w-12 object-cover border-2 border-[#00FFFF]/60 flex-shrink-0" />
            ) : (
              <div className="h-12 w-12 bg-[#00FFFF]/20 flex items-center justify-center text-[#00FFFF] font-bold border-2 border-[#00FFFF]/60 flex-shrink-0">
                {data?.user?.name?.[0]?.toUpperCase() || "?"}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tight leading-tight truncate">
                {data?.user?.name || `@${username}`}
              </h1>
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                admin · share activity · @{username}
              </p>
              <div className="mt-3">
                <AdminUserTabs username={username} active="shares" />
              </div>
            </div>
            {videos.length > 0 && (
              <button
                type="button"
                data-testid="export-share-csv"
                onClick={() => {
                  const headers = [
                    "video_id", "caption", "shares_by_user", "clicks_driven",
                    "total_share_count", "view_count", "last_shared_at",
                    "creator_username", "is_deleted",
                  ];
                  const rows = videos.map((v) => [
                    v.video_id, v.caption, v.shares_by_user, v.clicks_driven,
                    v.share_count, v.view_count, v.last_shared_at,
                    v.creator?.username || "", v.is_deleted ? "yes" : "no",
                  ]);
                  downloadCsv(`share-activity-${username}.csv`, headers, rows);
                }}
                title="Download CSV"
                className="ml-auto flex items-center gap-1.5 px-3 py-2 border border-[#00FFFF]/60 text-[#00FFFF] hover:bg-[#00FFFF]/10 font-mono text-[10px] uppercase tracking-widest whitespace-nowrap"
              >
                <Download className="h-3.5 w-3.5" /> csv
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-white/40">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : error ? (
          <div className="border border-[#FF007F]/40 bg-[#FF007F]/10 p-4 text-sm text-[#FF007F]">
            {error}
          </div>
        ) : (
          <>
            {/* Totals */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard testid="stat-total-shares" label="Total shares" value={fmtNum(totals.total_shares)} icon={<Share2 className="h-4 w-4" />} color="#00FFFF" />
              <StatCard testid="stat-clicks" label="Clicks driven" value={fmtNum(totals.total_clicks_driven)} icon={<MousePointerClick className="h-4 w-4" />} color="#FF007F" />
              <StatCard testid="stat-videos-shared" label="Videos shared" value={fmtNum(totals.unique_videos_shared)} icon={<Film className="h-4 w-4" />} color="#39FF14" />
              <StatCard testid="stat-platforms" label="Platforms used" value={fmtNum(Object.keys(platforms).length)} icon={<Share2 className="h-4 w-4" />} color="#FFC300" />
            </div>

            {/* Platform breakdown */}
            {platformSum > 0 && (
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/40 mb-2">// platform.breakdown</p>
                <div className="border border-white/10 divide-y divide-white/5">
                  {Object.entries(platforms)
                    .sort((a, b) => b[1] - a[1])
                    .map(([name, count]) => {
                      const pct = platformSum ? Math.round((count / platformSum) * 100) : 0;
                      const color = PLATFORM_COLORS[name] || PLATFORM_COLORS.unknown;
                      return (
                        <div key={name} className="px-3 py-2 flex items-center gap-3" data-testid={`platform-${name}`}>
                          <span className="w-24 font-mono text-[11px] uppercase tracking-widest text-white/70 truncate">{name}</span>
                          <div className="flex-1 h-1.5 bg-white/5">
                            <div className="h-full" style={{ width: `${pct}%`, background: color }} />
                          </div>
                          <span className="font-mono text-[11px] text-white/60 w-16 text-right tabular-nums">
                            {count} · {pct}%
                          </span>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Per-video table */}
            <div>
              <div className="flex items-center justify-between mb-2 gap-3">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/40">// per.video.breakdown</p>
                {totals.orphan_clicks > 0 && <OrphanClicksBadge count={totals.orphan_clicks} />}
              </div>
              {videos.length === 0 ? (
                <div className="border border-white/10 bg-white/[0.02] px-4 py-10 text-center text-white/40 text-sm">
                  No shares recorded for this user yet.
                </div>
              ) : (
                <div className="border border-white/10 divide-y divide-white/5">
                  {videos.map((v) => (
                    <VideoRow key={v.video_id} v={v} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ testid, label, value, icon, color }) {
  return (
    <div data-testid={testid} className="border border-white/10 bg-white/[0.02] p-3">
      <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-white/40 mb-1">
        <span style={{ color }}>{icon}</span>
        <span>{label}</span>
      </div>
      <div className="font-display text-2xl font-black leading-none" style={{ color }}>{value}</div>
    </div>
  );
}

// Explanatory badge for referral clicks that couldn't be paired with a
// specific per-video share row. Tapping reveals a short plain-English
// explainer so admins never have to guess where the number came from.
function OrphanClicksBadge({ count }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        data-testid="orphan-clicks-note"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest text-[#FF007F]/80 border border-[#FF007F]/40 px-2 py-1 hover:bg-[#FF007F]/10 transition-colors whitespace-nowrap"
      >
        <Info className="h-3 w-3" />
        +{fmtNum(count)} unattributed clicks
      </button>
      {open && (
        <div
          data-testid="orphan-clicks-modal"
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="max-w-sm w-full bg-[#0A0A0F] border border-[#FF007F]/50 p-5 text-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF007F]">
                // unattributed.clicks
              </p>
              <button
                onClick={() => setOpen(false)}
                data-testid="orphan-clicks-close"
                className="text-white/40 hover:text-white"
              >
                <XIcon className="h-4 w-4" />
              </button>
            </div>
            <p className="font-display font-bold text-lg mb-2 leading-tight">
              {fmtNum(count)} real clicks with no share row
            </p>
            <p className="text-sm text-white/70 leading-relaxed mb-3">
              These clicks are attributed to this user but don&apos;t tie back to a specific per-video share. Most common sources:
            </p>
            <ul className="text-xs text-white/60 space-y-2 list-disc pl-4 mb-3">
              <li>
                <span className="text-white/80">Legacy links.</span> Shares made before per-video tracking existed (early Feb 2026 and older).
              </li>
              <li>
                <span className="text-white/80">Native share sheet.</span> When Android/iOS pops the OS share menu, we log the click but not always the outbound app tap.
              </li>
              <li>
                <span className="text-white/80">Removed shares.</span> A share row (or the video) was deleted but the visits already fired.
              </li>
            </ul>
            <p className="text-[11px] text-white/40 leading-relaxed">
              These clicks are still counted in the Clicks Driven total above — they just don&apos;t show up in the per-video table because there&apos;s no video row to attach them to.
            </p>
          </div>
        </div>
      )}
    </>
  );
}

function VideoRow({ v }) {
  return (
    <div className="flex items-center gap-3 px-3 py-3" data-testid={`share-video-row-${v.video_id.slice(0, 8)}`}>
      <Link to={`/v/${v.video_id}`} className="flex-shrink-0 relative group">
        <img
          src={posterUrl(v.video_id)}
          alt=""
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
          className="h-14 w-14 object-cover border border-white/10 group-hover:border-[#39FF14]/60"
        />
        <div className="h-14 w-14 bg-white/5 items-center justify-center border border-white/10" style={{ display: "none" }}>
          <Play className="h-4 w-4 text-white/30" />
        </div>
        {v.is_deleted && (
          <span className="absolute inset-0 bg-black/70 flex items-center justify-center font-mono text-[8px] uppercase text-[#FF007F]">
            deleted
          </span>
        )}
      </Link>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-white/90 truncate">
          {v.caption || <span className="text-white/40 italic">(no caption)</span>}
        </p>
        <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest text-white/50 mt-1">
          <span className="text-[#00FFFF]">{fmtNum(v.shares_by_user)} shares</span>
          <span className="text-[#FF007F]">{fmtNum(v.clicks_driven)} clicks</span>
          {v.creator?.username && (
            <Link to={`/u/${v.creator.username}`} className="hover:text-white truncate" title={v.creator.name}>
              by @{v.creator.username}
            </Link>
          )}
        </div>
        {v.last_shared_at && (
          <p className="font-mono text-[9px] uppercase tracking-widest text-white/30 mt-0.5">
            last shared {fmtDate(v.last_shared_at)}
          </p>
        )}
      </div>
      <Link
        to={`/v/${v.video_id}`}
        className="text-white/40 hover:text-white flex-shrink-0"
        aria-label="Open video"
      >
        <ChevronRight className="h-5 w-5" />
      </Link>
    </div>
  );
}
